import React, { useState, useEffect, useCallback, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { getMyTickets, getTicket, replyTicket, cancelTicket } from "./supportClient";
import ContactModal from "./ContactModal";
import { useI18n } from "./i18n";

/* ─── Translations ────────────────────────────────────────────────────────── */
const TT = {
  /* Page header */
  "title.my":        { fr:"Mes",                              en:"My",                                  es:"Mis",                                 de:"Meine",                                 pt:"Meus",                                ar:"تذاكري" },
  "title.tickets":   { fr:"Tickets",                          en:"Tickets",                             es:"Tickets",                             de:"Tickets",                               pt:"Tickets",                             ar:"" },
  "sub.default":     { fr:"Gérez vos demandes auprès du support.", en:"Manage your support requests.",  es:"Gestiona tus solicitudes de soporte.",de:"Verwalten Sie Ihre Support-Anfragen.",  pt:"Gerencie suas solicitações de suporte.", ar:"إدارة طلبات الدعم الخاصة بك." },
  "sub.one":         { fr:"ticket",                           en:"ticket",                              es:"ticket",                              de:"Ticket",                                pt:"ticket",                              ar:"تذكرة" },
  "sub.many":        { fr:"tickets",                          en:"tickets",                             es:"tickets",                             de:"Tickets",                               pt:"tickets",                             ar:"تذاكر" },
  "sub.in_progress": { fr:"en cours",                         en:"in progress",                         es:"en curso",                            de:"in Bearbeitung",                        pt:"em andamento",                        ar:"قيد المعالجة" },
  "btn.new":         { fr:"+ Nouveau ticket",                 en:"+ New ticket",                        es:"+ Nuevo ticket",                      de:"+ Neues Ticket",                        pt:"+ Novo ticket",                       ar:"+ تذكرة جديدة" },

  /* Banners */
  "banner.login":    { fr:"Connectez-vous pour créer des tickets et suivre vos demandes auprès du support.",
                       en:"Sign in to create tickets and track your support requests.",
                       es:"Inicia sesión para crear tickets y seguir tus solicitudes de soporte.",
                       de:"Melden Sie sich an, um Tickets zu erstellen und Ihre Support-Anfragen zu verfolgen.",
                       pt:"Faça login para criar tickets e acompanhar suas solicitações de suporte.",
                       ar:"سجّل الدخول لإنشاء التذاكر ومتابعة طلبات الدعم." },
  "banner.login.btn":{ fr:"Se connecter",                     en:"Sign in",                             es:"Iniciar sesión",                      de:"Anmelden",                              pt:"Entrar",                              ar:"تسجيل الدخول" },
  "banner.locked.1": { fr:"Votre compte est désactivé. Vous ne pouvez pas créer ni consulter des tickets.",
                       en:"Your account is disabled. You cannot create or view tickets.",
                       es:"Tu cuenta está desactivada. No puedes crear ni ver tickets.",
                       de:"Ihr Konto ist deaktiviert. Sie können keine Tickets erstellen oder anzeigen.",
                       pt:"Sua conta está desativada. Você não pode criar nem consultar tickets.",
                       ar:"تم تعطيل حسابك. لا يمكنك إنشاء أو عرض التذاكر." },
  "banner.locked.2": { fr:"Contactez-nous à",                 en:"Contact us at",                       es:"Contáctanos en",                      de:"Kontaktieren Sie uns unter",            pt:"Entre em contato em",                 ar:"تواصل معنا على" },
  "banner.locked.3": { fr:"pour réactiver votre compte.",     en:"to reactivate your account.",         es:"para reactivar tu cuenta.",           de:"um Ihr Konto zu reaktivieren.",         pt:"para reativar sua conta.",            ar:"لإعادة تفعيل حسابك." },
  "banner.welcome":  { fr:"Bienvenue dans le support ! Vous pouvez créer un ticket pour toute question sur votre compte, vos dépôts, retraits ou les marchés.",
                       en:"Welcome to support! You can create a ticket for any question about your account, deposits, withdrawals or markets.",
                       es:"¡Bienvenido al soporte! Puedes crear un ticket para cualquier pregunta sobre tu cuenta, depósitos, retiros o mercados.",
                       de:"Willkommen beim Support! Sie können ein Ticket für jede Frage zu Konto, Einzahlungen, Auszahlungen oder Märkten erstellen.",
                       pt:"Bem-vindo ao suporte! Você pode criar um ticket para qualquer dúvida sobre sua conta, depósitos, retiradas ou mercados.",
                       ar:"مرحبًا بك في الدعم! يمكنك إنشاء تذكرة لأي سؤال يتعلق بحسابك أو الإيداعات أو السحوبات أو الأسواق." },
  "banner.welcome.btn":{ fr:"Créer un ticket",                en:"Create a ticket",                     es:"Crear un ticket",                     de:"Ticket erstellen",                      pt:"Criar um ticket",                     ar:"إنشاء تذكرة" },

  /* Filters */
  "filter.all":          { fr:"Tous",       en:"All",         es:"Todos",       de:"Alle",            pt:"Todos",       ar:"الكل" },
  "filter.open":         { fr:"Ouverts",    en:"Open",        es:"Abiertos",    de:"Offen",           pt:"Abertos",     ar:"مفتوحة" },
  "filter.in_progress":  { fr:"En cours",   en:"In progress", es:"En curso",    de:"In Bearbeitung",  pt:"Em andamento",ar:"قيد المعالجة" },
  "filter.resolved":     { fr:"Résolus",    en:"Resolved",    es:"Resueltos",   de:"Gelöst",          pt:"Resolvidos",  ar:"تم الحل" },
  "filter.closed":       { fr:"Fermés",     en:"Closed",      es:"Cerrados",    de:"Geschlossen",     pt:"Fechados",    ar:"مغلقة" },

  /* Status pill labels */
  "status.open":         { fr:"Ouvert",     en:"Open",        es:"Abierto",     de:"Offen",           pt:"Aberto",      ar:"مفتوحة" },
  "status.in_progress":  { fr:"En cours",   en:"In progress", es:"En curso",    de:"In Bearbeitung",  pt:"Em andamento",ar:"قيد المعالجة" },
  "status.resolved":     { fr:"Résolu",     en:"Resolved",    es:"Resuelto",    de:"Gelöst",          pt:"Resolvido",   ar:"تم الحل" },
  "status.closed":       { fr:"Fermé",      en:"Closed",      es:"Cerrado",     de:"Geschlossen",     pt:"Fechado",     ar:"مغلقة" },

  /* Priority pill labels */
  "priority.urgent":     { fr:"Urgent",     en:"Urgent",      es:"Urgente",     de:"Dringend",        pt:"Urgente",     ar:"عاجلة" },
  "priority.high":       { fr:"Haute",      en:"High",        es:"Alta",        de:"Hoch",            pt:"Alta",        ar:"عالية" },
  "priority.medium":     { fr:"Moyenne",    en:"Medium",      es:"Media",       de:"Mittel",          pt:"Média",       ar:"متوسطة" },
  "priority.low":        { fr:"Basse",      en:"Low",         es:"Baja",        de:"Niedrig",         pt:"Baixa",       ar:"منخفضة" },

  /* Empty / loading */
  "empty.all":           { fr:"Vous n'avez pas encore de ticket de support.",
                           en:"You don't have any support tickets yet.",
                           es:"Aún no tienes ningún ticket de soporte.",
                           de:"Sie haben noch keine Support-Tickets.",
                           pt:"Você ainda não tem nenhum ticket de suporte.",
                           ar:"ليس لديك أي تذكرة دعم بعد." },
  "empty.filter":        { fr:"Aucun ticket avec le statut", en:"No tickets with status",
                           es:"Ningún ticket con estado",    de:"Keine Tickets mit Status",
                           pt:"Nenhum ticket com status",    ar:"لا توجد تذاكر بحالة" },
  "empty.btn":           { fr:"Contacter le support →",      en:"Contact support →",
                           es:"Contactar al soporte →",      de:"Support kontaktieren →",
                           pt:"Contatar o suporte →",        ar:"← التواصل مع الدعم" },

  /* Card */
  "card.unassigned":     { fr:"En attente d'attribution",    en:"Awaiting assignment",
                           es:"Pendiente de asignación",      de:"Wartet auf Zuweisung",
                           pt:"Aguardando atribuição",        ar:"بانتظار التعيين" },
  "card.click_to_reply": { fr:"Cliquer pour répondre →",     en:"Click to reply →",
                           es:"Clic para responder →",        de:"Zum Antworten klicken →",
                           pt:"Clique para responder →",      ar:"← انقر للرد" },

  /* Detail view */
  "detail.back":         { fr:"← Mes tickets",      en:"← My tickets",         es:"← Mis tickets",        de:"← Meine Tickets",       pt:"← Meus tickets",        ar:"← تذاكري" },
  "detail.not_found":    { fr:"Ticket introuvable.",en:"Ticket not found.",    es:"Ticket no encontrado.",de:"Ticket nicht gefunden.",pt:"Ticket não encontrado.",ar:"التذكرة غير موجودة." },
  "detail.conversation": { fr:"Conversation",       en:"Conversation",         es:"Conversación",         de:"Konversation",          pt:"Conversa",              ar:"المحادثة" },
  "detail.initial":      { fr:"Votre demande initiale", en:"Your initial request", es:"Tu solicitud inicial", de:"Ihre ursprüngliche Anfrage", pt:"Sua solicitação inicial", ar:"طلبك الأولي" },
  "detail.support_tag":  { fr:"SUPPORT",            en:"SUPPORT",              es:"SOPORTE",              de:"SUPPORT",               pt:"SUPORTE",               ar:"الدعم" },
  "detail.reply":        { fr:"Votre réponse",      en:"Your reply",           es:"Tu respuesta",         de:"Ihre Antwort",          pt:"Sua resposta",          ar:"ردك" },
  "detail.placeholder":  { fr:"Ajoutez des informations ou répondez à l'agent support…",
                           en:"Add information or reply to the support agent…",
                           es:"Añade información o responde al agente de soporte…",
                           de:"Fügen Sie Informationen hinzu oder antworten Sie dem Support-Agenten…",
                           pt:"Adicione informações ou responda ao agente de suporte…",
                           ar:"أضف معلومات أو رد على وكيل الدعم…" },
  "detail.hint":         { fr:"Ctrl + Entrée pour envoyer", en:"Ctrl + Enter to send",
                           es:"Ctrl + Intro para enviar",    de:"Strg + Enter zum Senden",
                           pt:"Ctrl + Enter para enviar",    ar:"Ctrl + Enter للإرسال" },
  "detail.send":         { fr:"Envoyer ↵",      en:"Send ↵",       es:"Enviar ↵",     de:"Senden ↵",    pt:"Enviar ↵",   ar:"إرسال ↵" },
  "detail.sending":      { fr:"Envoi…",         en:"Sending…",     es:"Enviando…",    de:"Senden…",     pt:"Enviando…",  ar:"جارٍ الإرسال…" },
  "detail.resolved_msg": { fr:"✓ Ce ticket a été résolu. Si votre problème persiste, vous pouvez ouvrir un nouveau ticket.",
                           en:"✓ This ticket has been resolved. If your issue persists, you can open a new ticket.",
                           es:"✓ Este ticket ha sido resuelto. Si tu problema persiste, puedes abrir uno nuevo.",
                           de:"✓ Dieses Ticket wurde gelöst. Falls Ihr Problem weiterhin besteht, können Sie ein neues Ticket eröffnen.",
                           pt:"✓ Este ticket foi resolvido. Se o seu problema persistir, você pode abrir um novo ticket.",
                           ar:"✓ تم حل هذه التذكرة. إذا استمرت مشكلتك، يمكنك فتح تذكرة جديدة." },
  "detail.closed_msg":   { fr:"Ce ticket est fermé.",       en:"This ticket is closed.",
                           es:"Este ticket está cerrado.",   de:"Dieses Ticket ist geschlossen.",
                           pt:"Este ticket está fechado.",   ar:"هذه التذكرة مغلقة." },
  "detail.new":          { fr:"Nouveau ticket",  en:"New ticket",   es:"Nuevo ticket", de:"Neues Ticket", pt:"Novo ticket", ar:"تذكرة جديدة" },

  /* Time ago */
  "time.now":            { fr:"à l'instant",    en:"just now",     es:"justo ahora",  de:"gerade eben", pt:"agora mesmo", ar:"الآن" },

  /* Toasts */
  "toast.reply":         { fr:"Réponse envoyée.", en:"Reply sent.",
                           es:"Respuesta enviada.", de:"Antwort gesendet.",
                           pt:"Resposta enviada.",  ar:"تم إرسال الرد." },
  "toast.created":       { fr:"Ticket créé ! Notre équipe vous répondra sous 24h.",
                           en:"Ticket created! Our team will reply within 24 hours.",
                           es:"¡Ticket creado! Nuestro equipo responderá en 24 horas.",
                           de:"Ticket erstellt! Unser Team antwortet innerhalb von 24 Stunden.",
                           pt:"Ticket criado! Nossa equipe responderá em até 24 horas.",
                           ar:"تم إنشاء التذكرة! سيرد فريقنا خلال 24 ساعة." },

  /* Locale for date formatting */
  "locale":              { fr:"fr-FR", en:"en-US", es:"es-ES", de:"de-DE", pt:"pt-BR", ar:"ar-SA" },
};

function tt(key, lang) {
  const v = TT[key];
  if (!v) return key;
  return v[lang] ?? v.fr ?? key;
}

/* ─── Animations ─────────────────────────────────────────────────────────── */
const fadeUp  = keyframes`from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}`;
const shimmer = keyframes`0%{background-position:-400px 0}100%{background-position:400px 0}`;
const popIn   = keyframes`0%{transform:scale(.9);opacity:0}100%{transform:scale(1);opacity:1}`;

/* ─── Layout ──────────────────────────────────────────────────────────────── */
const Wrapper   = styled.div`max-width:900px;margin:0 auto;padding:2rem 2rem 4rem;`;
const PageHead  = styled.div`
  display:flex;align-items:flex-end;justify-content:space-between;
  margin-bottom:2rem;flex-wrap:wrap;gap:1rem;
`;
const PageTitle = styled.h1`
  font-family:${({theme})=>theme.fontDisplay};font-size:1.8rem;font-weight:700;
  letter-spacing:-.03em;span{color:${({theme})=>theme.accent};}
`;
const PageSub   = styled.p`font-size:.78rem;color:${({theme})=>theme.textMuted};margin-top:.25rem;`;
const NewBtn    = styled.button`
  display:flex;align-items:center;gap:.4rem;
  padding:.6rem 1.2rem;border-radius:8px;
  border:1px solid ${({theme})=>theme.accent}66;
  background:rgba(79,195,247,.08);color:${({theme})=>theme.accent};
  font-family:${({theme})=>theme.fontBody};font-size:.8rem;font-weight:600;
  cursor:pointer;transition:all .15s;&:hover{opacity:.8;}
`;

/* ─── User status banner ──────────────────────────────────────────────────── */
const BannerWrap = styled.div`
  display:flex;align-items:center;gap:1rem;padding:1rem 1.25rem;
  border-radius:12px;margin-bottom:1.5rem;
  background:${({$type})=>
    $type==="locked"   ?"rgba(255,59,92,.06)":
    $type==="login"    ?"rgba(79,195,247,.06)":
    "rgba(0,230,118,.06)"};
  border:1px solid ${({$type})=>
    $type==="locked"   ?"rgba(255,59,92,.2)":
    $type==="login"    ?"rgba(79,195,247,.2)":
    "rgba(0,230,118,.2)"};
`;
const BannerIcon  = styled.span`font-size:1.4rem;flex-shrink:0;`;
const BannerText  = styled.div`flex:1;font-size:.78rem;line-height:1.55;
  color:${({$type,theme})=>
    $type==="locked"?theme.red:$type==="login"?theme.accent:theme.green};
`;
const BannerBtn   = styled.button`
  padding:.45rem 1rem;border-radius:7px;font-size:.76rem;font-weight:600;
  font-family:${({theme})=>theme.fontBody};cursor:pointer;transition:all .15s;
  border:1px solid ${({$type})=>
    $type==="locked"?"rgba(255,59,92,.4)":"rgba(79,195,247,.4)"};
  background:transparent;
  color:${({$type,theme})=>$type==="locked"?theme.red:theme.accent};
  &:hover{opacity:.8;}
`;

/* ─── Filter tabs ─────────────────────────────────────────────────────────── */
const FilterRow = styled.div`display:flex;gap:.35rem;margin-bottom:1.25rem;flex-wrap:wrap;`;
const FilterBtn = styled.button`
  font-family:${({theme})=>theme.fontMono};font-size:.62rem;padding:.25rem .7rem;
  border-radius:100px;letter-spacing:.05em;cursor:pointer;transition:all .15s;
  border:1px solid ${({$a,theme})=>$a?theme.borderLight:theme.border};
  background:${({$a,theme})=>$a?theme.bgHover:"transparent"};
  color:${({$a,theme})=>$a?theme.text:theme.textMuted};
  &:hover{border-color:${({theme})=>theme.borderLight};}
`;

/* ─── Ticket list ─────────────────────────────────────────────────────────── */
const TicketList = styled.div`display:flex;flex-direction:column;gap:.6rem;`;
const TicketCard = styled.div`
  background:${({theme})=>theme.bgCard};border:1px solid ${({theme})=>theme.border};
  border-radius:12px;padding:1rem 1.25rem;cursor:pointer;
  transition:border-color .2s,transform .15s,box-shadow .15s;
  animation:${fadeUp} .3s ease both;animation-delay:${({$i})=>$i*.05}s;
  border-left:3px solid ${({$p,theme})=>
    $p==="urgent"?"#ff3b5c":$p==="high"?"#ff9800":$p==="medium"?theme.accent:theme.border};
  &:hover{border-color:${({theme})=>theme.borderLight};transform:translateX(3px);box-shadow:0 4px 20px rgba(0,0,0,.3);}
`;
const TCardTop  = styled.div`display:flex;align-items:flex-start;justify-content:space-between;gap:.75rem;margin-bottom:.5rem;`;
const TSubject  = styled.h3`font-size:.88rem;font-weight:600;line-height:1.3;flex:1;`;
const TCardBot  = styled.div`display:flex;align-items:center;gap:.75rem;flex-wrap:wrap;`;
const Pill      = styled.span`
  font-family:${({theme})=>theme.fontMono};font-size:.6rem;padding:.18rem .55rem;
  border-radius:100px;letter-spacing:.05em;
  background:${({$s,theme})=>
    $s==="open"        ?"rgba(0,230,118,.1)":
    $s==="in_progress" ?"rgba(255,193,7,.1)" :
    $s==="resolved"    ?"rgba(79,195,247,.1)":
    $s==="closed"      ?theme.bgHover        :
    $s==="urgent"      ?theme.redDim         :
    $s==="high"        ?"rgba(255,152,0,.1)" :theme.bgHover};
  color:${({$s,theme})=>
    $s==="open"        ?theme.green :
    $s==="in_progress" ?theme.gold  :
    $s==="resolved"    ?theme.accent:
    $s==="closed"      ?theme.textMuted:
    $s==="urgent"      ?theme.red   :
    $s==="high"        ?"#ff9800"   :theme.textMuted};
`;
const TMeta     = styled.span`font-family:${({theme})=>theme.fontMono};font-size:.63rem;color:${({theme})=>theme.textMuted};`;
const TAssign   = styled.span`font-size:.7rem;color:${({theme})=>theme.textDim};`;
const MsgBadge  = styled.span`
  font-family:${({theme})=>theme.fontMono};font-size:.62rem;
  background:${({theme})=>theme.bgHover};border:1px solid ${({theme})=>theme.border};
  border-radius:100px;padding:.15rem .5rem;color:${({theme})=>theme.textMuted};
`;

/* ─── Empty / loading ─────────────────────────────────────────────────────── */
const Empty     = styled.div`text-align:center;padding:3rem 1rem;display:flex;flex-direction:column;align-items:center;gap:.75rem;`;
const EmptyIcon = styled.div`font-size:2.5rem;`;
const EmptyTxt  = styled.p`font-size:.82rem;color:${({theme})=>theme.textMuted};`;
const Skel      = styled.div`height:90px;border-radius:12px;background:linear-gradient(90deg,${({theme})=>theme.bgCard} 25%,${({theme})=>theme.bgHover} 50%,${({theme})=>theme.bgCard} 75%);background-size:400px 100%;animation:${shimmer} 1.4s infinite;margin-bottom:.6rem;`;

/* ─── Ticket detail ───────────────────────────────────────────────────────── */
const DetailWrap   = styled.div`animation:${fadeUp} .25s ease;`;
const BackBtn      = styled.button`
  background:none;border:none;color:${({theme})=>theme.textMuted};
  font-size:.8rem;cursor:pointer;margin-bottom:1.25rem;
  display:flex;align-items:center;gap:.4rem;padding:0;
  transition:color .15s;&:hover{color:${({theme})=>theme.text};}
`;
const DetailHead   = styled.div`
  background:${({theme})=>theme.bgCard};border:1px solid ${({theme})=>theme.border};
  border-radius:12px;padding:1.25rem;margin-bottom:1.25rem;
  border-left:4px solid ${({$p,theme})=>
    $p==="urgent"?"#ff3b5c":$p==="high"?"#ff9800":$p==="medium"?theme.accent:theme.border};
`;
const DetailTitle  = styled.h2`font-family:${({theme})=>theme.fontDisplay};font-size:1.15rem;font-weight:700;letter-spacing:-.02em;margin-bottom:.5rem;`;
const DetailMeta   = styled.div`display:flex;gap:.5rem;flex-wrap:wrap;align-items:center;`;
const MetaItem     = styled.span`font-size:.72rem;color:${({theme})=>theme.textMuted};display:flex;align-items:center;gap:.25rem;`;
const ConvoWrap    = styled.div`background:${({theme})=>theme.bgCard};border:1px solid ${({theme})=>theme.border};border-radius:12px;overflow:hidden;margin-bottom:1.25rem;`;
const ConvoHead    = styled.div`padding:.8rem 1.25rem;background:${({theme})=>theme.bg};border-bottom:1px solid ${({theme})=>theme.border};font-family:${({theme})=>theme.fontMono};font-size:.65rem;text-transform:uppercase;letter-spacing:.08em;color:${({theme})=>theme.textMuted};`;
const MsgList      = styled.div`padding:1rem 1.25rem;display:flex;flex-direction:column;gap:.9rem;max-height:440px;overflow-y:auto;`;
const MsgWrap      = styled.div`display:flex;flex-direction:${({$emp})=>$emp?"row-reverse":"row"};gap:.6rem;align-items:flex-end;`;
const MsgAvatar    = styled.div`
  width:30px;height:30px;border-radius:50%;flex-shrink:0;
  background:${({$emp,theme})=>$emp?"#1976d2":theme.bgHover};
  border:1px solid ${({theme})=>theme.border};
  display:flex;align-items:center;justify-content:center;
  font-family:${({theme})=>theme.fontMono};font-size:.62rem;font-weight:600;
`;
const MsgBubble    = styled.div`
  max-width:78%;background:${({$emp,theme})=>$emp?theme.bgHover:theme.bg};
  border:1px solid ${({theme})=>theme.border};
  border-radius:${({$emp})=>$emp?"12px 2px 12px 12px":"2px 12px 12px 12px"};
  padding:.65rem .9rem;
`;
const MsgAuthor    = styled.div`font-size:.7rem;font-weight:600;margin-bottom:.2rem;color:${({$emp,theme})=>$emp?theme.accent:theme.text};display:flex;align-items:center;gap:.4rem;`;
const SupportTag   = styled.span`font-family:${({theme})=>theme.fontMono};font-size:.55rem;padding:.1rem .4rem;border-radius:3px;background:rgba(79,195,247,.15);color:${({theme})=>theme.accent};`;
const MsgText      = styled.p`font-size:.8rem;line-height:1.55;color:${({theme})=>theme.text};white-space:pre-wrap;`;
const MsgTime      = styled.span`font-family:${({theme})=>theme.fontMono};font-size:.58rem;color:${({theme})=>theme.textMuted};`;
const DescBlock    = styled.div`background:rgba(79,195,247,.05);border:1px solid rgba(79,195,247,.15);border-radius:8px;padding:.85rem 1rem;margin-bottom:.5rem;`;
const DescLabel    = styled.div`font-size:.68rem;color:${({theme})=>theme.accent};font-weight:600;margin-bottom:.35rem;`;
const ReplyWrap    = styled.div`background:${({theme})=>theme.bgCard};border:1px solid ${({theme})=>theme.border};border-radius:12px;overflow:hidden;`;
const ReplyHead    = styled.div`padding:.7rem 1.25rem;background:${({theme})=>theme.bg};border-bottom:1px solid ${({theme})=>theme.border};font-family:${({theme})=>theme.fontMono};font-size:.65rem;text-transform:uppercase;letter-spacing:.08em;color:${({theme})=>theme.textMuted};`;
const ReplyBody    = styled.div`padding:.85rem 1.25rem 1rem;display:flex;flex-direction:column;gap:.6rem;`;
const ReplyArea    = styled.textarea`
  width:100%;background:${({theme})=>theme.bg};border:1px solid ${({theme})=>theme.border};
  border-radius:8px;color:${({theme})=>theme.text};font-family:${({theme})=>theme.fontBody};
  font-size:.82rem;line-height:1.55;padding:.65rem .85rem;outline:none;resize:none;min-height:90px;
  transition:border-color .15s;
  &:focus{border-color:${({theme})=>theme.borderLight};}
  &::placeholder{color:${({theme})=>theme.textMuted};}
`;
const ReplyFoot    = styled.div`display:flex;align-items:center;justify-content:space-between;`;
const ReplySend    = styled.button`
  padding:.55rem 1.2rem;border-radius:7px;border:none;
  background:${({theme,disabled})=>disabled?theme.bgHover:theme.accent};
  color:${({theme,disabled})=>disabled?theme.textMuted:"#000"};
  font-family:${({theme})=>theme.fontBody};font-size:.8rem;font-weight:700;
  cursor:${({disabled})=>disabled?"not-allowed":"pointer"};transition:all .15s;
  &:hover:not(:disabled){opacity:.88;}
`;
const ReplyHint    = styled.span`font-family:${({theme})=>theme.fontMono};font-size:.62rem;color:${({theme})=>theme.textMuted};`;
const ClosedBanner = styled.div`
  background:${({theme})=>theme.bgHover};border:1px solid ${({theme})=>theme.border};
  border-radius:8px;padding:.75rem 1rem;font-size:.78rem;
  color:${({theme})=>theme.textMuted};text-align:center;
`;

/* ─── Toast interne ───────────────────────────────────────────────────────── */
const Toast = styled.div`
  position:fixed;bottom:1.5rem;right:1.5rem;z-index:9999;
  background:${({theme})=>theme.bgCard};
  border:1px solid ${({$ok,theme})=>$ok?theme.green+"55":theme.red+"55"};
  border-radius:10px;padding:.75rem 1rem;
  color:${({$ok,theme})=>$ok?theme.green:theme.red};
  font-size:.78rem;box-shadow:0 6px 24px rgba(0,0,0,.4);
  animation:${popIn} .2s ease;
`;

/* ─── Helpers ─────────────────────────────────────────────────────────────── */
function timeAgo(iso, lang) {
  if (!iso) return "—";
  const d = (Date.now() - new Date(iso)) / 1000;
  if (d < 60)    return tt("time.now", lang);
  if (d < 3600)  return formatAgo(Math.floor(d/60), "m", lang);
  if (d < 86400) return formatAgo(Math.floor(d/3600), "h", lang);
  return new Date(iso).toLocaleDateString(tt("locale", lang), { day:"2-digit", month:"short" });
}

function formatAgo(n, unit, lang) {
  switch (lang) {
    case "en": return `${n}${unit} ago`;
    case "es": return `hace ${n}${unit}`;
    case "de": return `vor ${n}${unit}`;
    case "pt": return `há ${n}${unit}`;
    case "ar": return `منذ ${n}${unit}`;
    default:   return `il y a ${n}${unit}`; // fr
  }
}

function initials(n = "") { return n.slice(0, 2).toUpperCase(); }

/* Status & priority labels: use API-provided label if present, else translate */
const labelStatus   = (t, lang) => t.status_label   || tt(`status.${t.status}`, lang);
const labelPriority = (p, lang) => tt(`priority.${p}`, lang) !== `priority.${p}` ? tt(`priority.${p}`, lang) : p;

const FILTERS = ["ALL", "open", "in_progress", "resolved", "closed"];

const MOCK_TICKETS = [
  { id:"t1", subject:"Dépôt non crédité depuis 2h",        category:"deposit",    category_label:"Dépôt",    priority:"high",   status:"in_progress", assigned_to:"Agent Support", message_count:1, created_at:new Date(Date.now()-7200000).toISOString() },
  { id:"t2", subject:"Problème pour retirer mes fonds EVP",category:"withdrawal", category_label:"Retrait",  priority:"medium", status:"open",        assigned_to:null,            message_count:0, created_at:new Date(Date.now()-86400000).toISOString() },
  { id:"t3", subject:"Bug sur la page de trading",          category:"bug",        category_label:"Bug",      priority:"low",    status:"resolved",    assigned_to:"Agent Support", message_count:4, created_at:new Date(Date.now()-172800000).toISOString() },
];

/* ─── TicketDetail ────────────────────────────────────────────────────────── */
function TicketDetail({ ticketId, user, onBack, onNewTicket }) {
  const { lang } = useI18n();
  const tr = useCallback((key) => tt(key, lang), [lang]);

  const [ticket,  setTicket]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply,   setReply]   = useState("");
  const [sending, setSending] = useState(false);
  const [toast,   setToast]   = useState(null);
  const bottomRef = useRef(null);

  const showToast = (msg, ok=true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getTicket(ticketId);
      setTicket(res.data);
    } catch {
      setTicket({
        id: ticketId, subject:"Dépôt non crédité depuis 2h",
        description:"J'ai effectué un dépôt EVP de 500$ il y a 2 heures et mon solde n'a toujours pas été mis à jour.",
        category:"deposit", category_label:"Dépôt",
        priority:"high", status:"in_progress",
        assigned_to:"Agent Support",
        created_at:new Date(Date.now()-7200000).toISOString(),
        messages:[
          { id:1, author:"Agent Support", is_employee:true,
            content:"Bonjour ! Nous avons bien reçu votre demande. Pouvez-vous nous indiquer le réseau utilisé (Ethereum, Polygon…) ?",
            created_at:new Date(Date.now()-3600000).toISOString() },
        ],
      });
    } finally { setLoading(false); }
  }, [ticketId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior:"smooth" }); }, [ticket?.messages?.length]);

  const sendReply = async () => {
    if (!reply.trim() || sending) return;
    setSending(true);
    const content = reply.trim();
    try {
      const res = await replyTicket(ticketId, { content });
      setTicket(t => ({ ...t, messages:[...(t.messages||[]), res.data] }));
      showToast(tr("toast.reply"));
    } catch {
      // Optimistic fallback
      setTicket(t => ({
        ...t,
        messages:[...(t.messages||[]), {
          id:Date.now(), author:user?.username||"Moi",
          is_employee:false, content, created_at:new Date().toISOString(),
        }],
      }));
      showToast(tr("toast.reply"));
    }
    setReply(""); setSending(false);
  };

  if (loading) return (
    <DetailWrap>
      <BackBtn onClick={onBack}>{tr("detail.back")}</BackBtn>
      <Skel style={{height:"120px",marginBottom:"1rem"}}/>
      <Skel style={{height:"300px"}}/>
    </DetailWrap>
  );

  if (!ticket) return (
    <DetailWrap>
      <BackBtn onClick={onBack}>{tr("detail.back")}</BackBtn>
      <p style={{color:"#5a6a7a"}}>{tr("detail.not_found")}</p>
    </DetailWrap>
  );

  const isClosed = ticket.status==="closed" || ticket.status==="resolved";

  return (
    <DetailWrap>
      <BackBtn onClick={onBack}>{tr("detail.back")}</BackBtn>

      <DetailHead $p={ticket.priority}>
        <DetailTitle>{ticket.subject}</DetailTitle>
        <DetailMeta>
          <Pill $s={ticket.status}>{labelStatus(ticket, lang)}</Pill>
          <Pill $s={ticket.priority}>{labelPriority(ticket.priority, lang)}</Pill>
          <MetaItem>📂 {ticket.category_label||ticket.category}</MetaItem>
          {ticket.assigned_to && <MetaItem>🎧 {ticket.assigned_to}</MetaItem>}
          <MetaItem>🕒 {timeAgo(ticket.created_at, lang)}</MetaItem>
        </DetailMeta>
      </DetailHead>

      <ConvoWrap>
        <ConvoHead>{tr("detail.conversation")}</ConvoHead>
        <MsgList>
          <DescBlock>
            <DescLabel>{tr("detail.initial")}</DescLabel>
            <p style={{fontSize:".8rem",lineHeight:1.55,color:"#8a9aaa"}}>{ticket.description}</p>
          </DescBlock>
          {(ticket.messages||[]).map(m=>(
            <MsgWrap key={m.id} $emp={m.is_employee}>
              <MsgAvatar $emp={m.is_employee}>{initials(m.author)}</MsgAvatar>
              <MsgBubble $emp={m.is_employee}>
                <MsgAuthor $emp={m.is_employee}>
                  {m.author}
                  {m.is_employee && <SupportTag>{tr("detail.support_tag")}</SupportTag>}
                  <MsgTime>{timeAgo(m.created_at, lang)}</MsgTime>
                </MsgAuthor>
                <MsgText>{m.content}</MsgText>
              </MsgBubble>
            </MsgWrap>
          ))}
          <div ref={bottomRef}/>
        </MsgList>
      </ConvoWrap>

      {isClosed ? (
        <ClosedBanner>
          {ticket.status==="resolved" ? tr("detail.resolved_msg") : tr("detail.closed_msg")}
          {ticket.status==="resolved" && (
            <button onClick={onNewTicket} style={{marginLeft:"1rem",background:"none",
              border:"1px solid rgba(79,195,247,.4)",color:"#4fc3f7",borderRadius:"6px",
              padding:".25rem .7rem",cursor:"pointer",fontSize:".75rem",fontFamily:"DM Mono"}}>
              {tr("detail.new")}
            </button>
          )}
        </ClosedBanner>
      ) : (
        <ReplyWrap>
          <ReplyHead>{tr("detail.reply")}</ReplyHead>
          <ReplyBody>
            <ReplyArea
              placeholder={tr("detail.placeholder")}
              value={reply}
              onChange={e=>setReply(e.target.value)}
              onKeyDown={e=>e.key==="Enter"&&e.ctrlKey&&sendReply()}
            />
            <ReplyFoot>
              <ReplyHint>{tr("detail.hint")}</ReplyHint>
              <ReplySend disabled={!reply.trim()||sending} onClick={sendReply}>
                {sending ? tr("detail.sending") : tr("detail.send")}
              </ReplySend>
            </ReplyFoot>
          </ReplyBody>
        </ReplyWrap>
      )}

      {toast && <Toast $ok={toast.ok}>{toast.ok?"✓":"✕"} {toast.msg}</Toast>}
    </DetailWrap>
  );
}

/* ─── Main component ──────────────────────────────────────────────────────── */
export default function MyTickets({ user, onLoginClick }) {
  const { lang } = useI18n();
  const tr = useCallback((key) => tt(key, lang), [lang]);

  const [tickets,    setTickets]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [filter,     setFilter]     = useState("ALL");
  const [openId,     setOpenId]     = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [toast,      setToast]      = useState(null);

  // Vérification : l'utilisateur est-il actif ?
  const isLoggedIn  = !!user;
  const isActive    = isLoggedIn && user.is_active !== false;  // actif par défaut si le champ n'existe pas

  const showToast = (msg, ok=true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3500);
  };

  const load = useCallback(async () => {
    if (!isActive) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await getMyTickets();
      setTickets(res.data?.results || res.data || []);
    } catch {
      // Fallback démo
      setTickets(MOCK_TICKETS);
    } finally { setLoading(false); }
  }, [isActive]);

  useEffect(() => { load(); }, [load]);

  const handleTicketCreated = () => {
    setShowCreate(false);
    showToast(tr("toast.created"));
    load();  // rafraîchir la liste
  };

  const filtered   = tickets.filter(t => filter==="ALL" || t.status===filter);
  const openCount  = tickets.filter(t => t.status==="open"||t.status==="in_progress").length;

  /* ── Vue détail d'un ticket ── */
  if (openId) return (
    <Wrapper>
      <TicketDetail
        ticketId={openId}
        user={user}
        onBack={()=>setOpenId(null)}
        onNewTicket={()=>{ setOpenId(null); setShowCreate(true); }}
      />
      {showCreate && (
        <ContactModal
          user={user}
          onClose={()=>setShowCreate(false)}
          onSuccess={handleTicketCreated}
        />
      )}
      {toast && <Toast $ok={toast.ok}>{toast.ok?"✓":"✕"} {toast.msg}</Toast>}
    </Wrapper>
  );

  /* ── Vue liste ── */
  return (
    <Wrapper>
      <PageHead>
        <div>
          <PageTitle>{tr("title.my")} <span>{tr("title.tickets")}</span></PageTitle>
          <PageSub>
            {isActive
              ? `${tickets.length} ${tickets.length>1?tr("sub.many"):tr("sub.one")} — ${openCount} ${tr("sub.in_progress")}`
              : tr("sub.default")}
          </PageSub>
        </div>
        {/* Bouton Nouveau ticket — affiché uniquement si actif */}
        {isActive && (
          <NewBtn onClick={()=>setShowCreate(true)}>{tr("btn.new")}</NewBtn>
        )}
      </PageHead>

      {/* ── Bannière selon l'état de l'utilisateur ── */}
      {!isLoggedIn && (
        <BannerWrap $type="login">
          <BannerIcon>🔒</BannerIcon>
          <BannerText $type="login">
            {tr("banner.login")}
          </BannerText>
          <BannerBtn $type="login" onClick={onLoginClick}>{tr("banner.login.btn")}</BannerBtn>
        </BannerWrap>
      )}

      {isLoggedIn && !isActive && (
        <BannerWrap $type="locked">
          <BannerIcon>⚠️</BannerIcon>
          <BannerText $type="locked">
            {tr("banner.locked.1")}<br/>
            {tr("banner.locked.2")} <strong>support@evptrading.com</strong> {tr("banner.locked.3")}
          </BannerText>
        </BannerWrap>
      )}

      {/* ── Contenu principal (utilisateurs actifs uniquement) ── */}
      {isActive && (
        <>
          {/* Hint création rapide si aucun ticket */}
          {!loading && tickets.length === 0 && (
            <BannerWrap $type="ok" style={{marginBottom:"1.25rem"}}>
              <BannerIcon>💡</BannerIcon>
              <BannerText $type="ok">
                {tr("banner.welcome")}
              </BannerText>
              <BannerBtn $type="login" onClick={()=>setShowCreate(true)}>{tr("banner.welcome.btn")}</BannerBtn>
            </BannerWrap>
          )}

          <FilterRow>
            {FILTERS.map(f=>(
              <FilterBtn key={f} $a={filter===f} onClick={()=>setFilter(f)}>
                {tr(`filter.${f.toLowerCase()}`)}
                {f==="ALL" && tickets.length>0 && ` (${tickets.length})`}
                {f==="open" && openCount>0 && (
                  <span style={{marginLeft:".3rem",background:"rgba(0,230,118,.2)",
                    borderRadius:"100px",padding:"0 .35rem",color:"#00e676",fontSize:".58rem"}}>
                    {openCount}
                  </span>
                )}
              </FilterBtn>
            ))}
          </FilterRow>

          {loading ? (
            <TicketList>{[1,2,3].map(i=><Skel key={i}/>)}</TicketList>
          ) : filtered.length === 0 ? (
            <Empty>
              <EmptyIcon>🎫</EmptyIcon>
              <EmptyTxt>
                {filter==="ALL"
                  ? tr("empty.all")
                  : `${tr("empty.filter")} "${tr(`status.${filter}`)}".`}
              </EmptyTxt>
              {filter==="ALL" && (
                <NewBtn onClick={()=>setShowCreate(true)}>{tr("empty.btn")}</NewBtn>
              )}
            </Empty>
          ) : (
            <TicketList>
              {filtered.map((t,i)=>(
                <TicketCard key={t.id} $i={i} $p={t.priority} onClick={()=>setOpenId(t.id)}>
                  <TCardTop>
                    <TSubject>{t.subject}</TSubject>
                    <Pill $s={t.status}>{labelStatus(t, lang)}</Pill>
                  </TCardTop>
                  <TCardBot>
                    <Pill $s={t.priority}>{labelPriority(t.priority, lang)}</Pill>
                    <TMeta>📂 {t.category_label||t.category}</TMeta>
                    <TMeta>🕒 {timeAgo(t.created_at, lang)}</TMeta>
                    {t.assigned_to
                      ? <TAssign>🎧 {t.assigned_to}</TAssign>
                      : <TAssign style={{color:"#5a6a7a"}}>{tr("card.unassigned")}</TAssign>}
                    {t.message_count>0 && <MsgBadge>💬 {t.message_count}</MsgBadge>}
                    {(t.status==="open"||t.status==="in_progress") && (
                      <span style={{marginLeft:"auto",fontSize:".62rem",color:"#5a6a7a",
                        fontFamily:"DM Mono,monospace"}}>
                        {tr("card.click_to_reply")}
                      </span>
                    )}
                  </TCardBot>
                </TicketCard>
              ))}
            </TicketList>
          )}
        </>
      )}

      {/* Modal création de ticket */}
      {showCreate && isActive && (
        <ContactModal
          user={user}
          onClose={()=>setShowCreate(false)}
          onSuccess={handleTicketCreated}
        />
      )}

      {toast && <Toast $ok={toast.ok}>{toast.ok?"✓":"✕"} {toast.msg}</Toast>}
    </Wrapper>
  );
}
